import React, { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, UserCog, Shield } from "lucide-react";

const UsersPage = () => {
  const { API, getAuthHeader, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    name: "",
    role: "staff",
  });

  const roles = ["admin", "manager", "staff"];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`, getAuthHeader());
      setUsers(response.data);
    } catch (error) {
      console.error("Fetch users error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingUser) {
        const updateData = { name: formData.name, role: formData.role };
        await axios.put(`${API}/users/${editingUser.id}`, updateData, getAuthHeader());
        toast.success("User updated successfully");
      } else {
        await axios.post(`${API}/users`, formData, getAuthHeader());
        toast.success("User created successfully");
      }

      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save user");
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      email: user.email,
      password: "",
      name: user.name,
      role: user.role,
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeader());
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete user");
    }
  };

  const toggleActive = async (user) => {
    try {
      await axios.put(`${API}/users/${user.id}`, { is_active: !user.is_active }, getAuthHeader());
      toast.success(`User ${user.is_active ? "deactivated" : "activated"} successfully`);
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update user status");
    }
  };

  const resetForm = () => {
    setEditingUser(null);
    setFormData({
      email: "",
      password: "",
      name: "",
      role: "staff",
    });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getRoleColor = (role) => {
    switch (role) {
      case "admin":
        return "bg-red-100 text-red-700";
      case "manager":
        return "bg-blue-100 text-blue-700";
      default:
        return "bg-zinc-100 text-zinc-700";
    }
  };

  const isAdmin = currentUser?.role === "admin";

  if (!isAdmin && currentUser?.role !== "manager") {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Shield className="w-12 h-12 text-zinc-300 mx-auto mb-4" />
          <p className="text-zinc-500">You don't have permission to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-zinc-950" data-testid="users-title">
            User Management
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            Manage system users and their roles
          </p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A192F] hover:bg-zinc-800 rounded-sm" data-testid="add-user-button">
                <Plus className="w-4 h-4 mr-2" />
                Add User
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>{editingUser ? "Edit User" : "Add New User"}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="user@example.com"
                    disabled={!!editingUser}
                    required={!editingUser}
                    data-testid="user-email-input"
                  />
                </div>

                {!editingUser && (
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-wider">Password</Label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="••••••••"
                      required
                      data-testid="user-password-input"
                    />
                  </div>
                )}

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Name</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Full name"
                    required
                    data-testid="user-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-wider">Role</Label>
                  <Select value={formData.role} onValueChange={(v) => setFormData({...formData, role: v})}>
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roles.map((role) => (
                        <SelectItem key={role} value={role} className="capitalize">
                          {role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="flex justify-end gap-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-[#0A192F] hover:bg-zinc-800" data-testid="user-save-button">
                    {editingUser ? "Update" : "Create"}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Users Table */}
      <Card className="border-zinc-200 shadow-none">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-zinc-50/50">
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">User</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Email</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Role</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Status</TableHead>
                <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider">Joined</TableHead>
                {isAdmin && <TableHead className="text-xs font-medium text-zinc-500 uppercase tracking-wider text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                    Loading users...
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-zinc-500">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.id} className="hover:bg-zinc-50" data-testid={`user-row-${user.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-[#0A192F] flex items-center justify-center text-sm font-medium text-white">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-zinc-900">{user.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-600">{user.email}</TableCell>
                    <TableCell>
                      <Badge className={`text-xs capitalize ${getRoleColor(user.role)}`}>
                        {user.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={user.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                        {user.is_active ? "Active" : "Inactive"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm text-zinc-500">{formatDate(user.created_at)}</TableCell>
                    {isAdmin && (
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleEdit(user)} data-testid={`edit-user-${user.id}`}>
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => toggleActive(user)}
                            data-testid={`toggle-user-${user.id}`}
                          >
                            <UserCog className="w-4 h-4" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button variant="ghost" size="sm" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Role Permissions Info */}
      <Card className="border-zinc-200 shadow-none">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium text-zinc-700 uppercase tracking-wider">
            Role Permissions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="p-4 border border-zinc-200 space-y-2">
              <Badge className="bg-red-100 text-red-700">Admin</Badge>
              <ul className="text-sm text-zinc-600 space-y-1">
                <li>• Full system access</li>
                <li>• User management</li>
                <li>• Service & pricing management</li>
                <li>• Delete records</li>
              </ul>
            </div>
            <div className="p-4 border border-zinc-200 space-y-2">
              <Badge className="bg-blue-100 text-blue-700">Manager</Badge>
              <ul className="text-sm text-zinc-600 space-y-1">
                <li>• View all data</li>
                <li>• Manage services</li>
                <li>• Generate QR codes</li>
                <li>• View reports</li>
              </ul>
            </div>
            <div className="p-4 border border-zinc-200 space-y-2">
              <Badge className="bg-zinc-100 text-zinc-700">Staff</Badge>
              <ul className="text-sm text-zinc-600 space-y-1">
                <li>• View dashboard</li>
                <li>• View customers</li>
                <li>• View bookings</li>
                <li>• Limited reports</li>
              </ul>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default UsersPage;
